import { useEffect, useRef, useState } from 'react';
import { api } from '../../../shared/api.js';
import { useSocketEvent } from '../../../shared/socket.js';

const MAX_EVENTS = 50;

/**
 * شكل واحد للحدث مهما كان مصدره.
 *
 * REST (`/gates/events/recent`) يعيد مستند الحدث مع `childId` مُحمَّلاً كمستند
 * طفل، أما البثّ الحيّ فيُرسل `{ event, gate, card, child }`. الشاشة تقرأ
 * `event.child` و`event.result` مباشرة — فبلا تطبيع كان السطر الحيّ يصل فارغاً
 * (لا اسم ولا حالة) حتى أول إعادة تحميل.
 */
function normalizeEvent(raw) {
  if (!raw) return null;
  const base = raw.event && typeof raw.event === 'object' ? raw.event : raw;
  const childDoc = raw.child
    || (base.child && typeof base.child === 'object' ? base.child : null)
    || (base.childId && typeof base.childId === 'object' ? base.childId : null);
  const sessionDoc = base.sessionId && typeof base.sessionId === 'object' ? base.sessionId : null;

  return {
    ...base,
    _id: base._id || base.id || null,
    gateName: base.gateName || raw.gate?.name || '',
    cardUid: base.cardUid || raw.card?.uid || null,
    child: childDoc || null,
    childId: childDoc ? (childDoc._id || childDoc.id) : (base.childId || raw.childId || null),
    sessionId: sessionDoc ? sessionDoc._id : (base.sessionId || raw.sessionId || null),
    // دخول/خروج باشتراك شهري — شارة في سجلّ الحركة.
    subscriptionCovered: base.subscriptionCovered
      ?? raw.subscriptionCovered
      ?? (sessionDoc ? sessionDoc.subscriptionCovered : undefined)
  };
}

export function useGateEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seenIds = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/gates/events/recent', { params: { limit: 30 } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.events || data?.data || data || [];
        const normalized = (Array.isArray(list) ? list : []).map(normalizeEvent).filter(Boolean);
        for (const e of normalized) {
          if (e._id) seenIds.current.add(e._id);
        }
        setEvents(normalized);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useSocketEvent('gate', 'event', (payload) => {
    const event = normalizeEvent(payload);
    if (!event) return;
    if (event._id) {
      if (seenIds.current.has(event._id)) return;
      seenIds.current.add(event._id);
    }
    setEvents((prev) => [event, ...prev].slice(0, MAX_EVENTS));
  });

  return { events, loading, error };
}
